// src/utils/notifyWebPush.js
// FlowSense Notify — browser push on this device (service worker + W3C Push API, VAPID).
// No Firebase: FlowSense signs every push with its own VAPID key (GET /notify/webpush/public-key).
import * as api from '../services/notifyApi';

const SW_URL = '/notify-sw.js';
const FLAG = 'fsn:webpush';

export const isSupported = () => typeof window !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const permission = () => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

const safeStore = {
    get: () => { try { return window.localStorage.getItem(FLAG); } catch (_) { return null; } },
    set: (v) => { try { if (v) window.localStorage.setItem(FLAG, v); else window.localStorage.removeItem(FLAG); } catch (_) { /* private mode */ } },
};

/** True when this browser has an active FlowSense push subscription (used to avoid duplicate desktop pop-ups). */
export const isEnabledHere = () => Boolean(safeStore.get());

function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(b64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

async function registration() {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    return existing || navigator.serviceWorker.register(SW_URL, { scope: '/' });
}

/** Current subscription state for the settings screen. */
export async function getState() {
    if (!isSupported()) return { supported: false, enabled: false, permission: 'unsupported' };
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!sub && safeStore.get()) safeStore.set(null);
        return { supported: true, enabled: Boolean(sub), permission: permission(), endpoint: sub?.endpoint || null };
    } catch (_) {
        return { supported: true, enabled: false, permission: permission() };
    }
}

/** Asks for permission, subscribes and registers the device with FlowSense. */
export async function enable({ deviceName } = {}) {
    if (!isSupported()) throw new Error('This browser does not support push notifications.');
    const key = await api.getWebPushKey();
    if (!key?.enabled || !key.publicKey) throw new Error(key?.reason ? `Browser push is not available on the server (${key.reason}).` : 'Browser push is not configured on the server.');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error(perm === 'denied' ? 'Notifications are blocked for FlowSense in this browser’s site settings.' : 'Permission was not granted.');
    const reg = await registration();
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    // A subscription made with another key cannot be reused
    if (sub && sub.options?.applicationServerKey) {
        const current = btoa(String.fromCharCode(...new Uint8Array(sub.options.applicationServerKey))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        if (current !== key.publicKey) { await sub.unsubscribe().catch(() => {}); sub = null; }
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key.publicKey) });
    const json = sub.toJSON();
    const device = await api.registerDevice({
        transport: 'webpush',
        platform: 'web',
        subscription: { endpoint: json.endpoint, keys: json.keys },
        deviceName: deviceName || describeBrowser(),
    });
    safeStore.set(String(device?.id || '1'));
    return device;
}

export async function disable() {
    if (!isSupported()) return false;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
        await api.unregisterEndpoint(sub.endpoint).catch(() => {});
        await sub.unsubscribe().catch(() => {});
    }
    safeStore.set(null);
    return true;
}

export function describeBrowser() {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} on ${os}` : browser;
}
